export const TICKET_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'NEEDS_INFO',
  'TRIAGED',
  'AWAITING_EXPERT',
  'EXPERT_REVIEWING',
  'RESPONSE_DRAFTING',
  'RESPONSE_READY',
  'RESPONDED',
  'AWAITING_REPLY',
  'CLOSED',
  'ARCHIVED',
] as const

export type TicketStatus = typeof TICKET_STATUSES[number]

// Exhaustive map of every allowed transition — any pair not listed here is forbidden.
// Based on §4.3 + §4.4 of docs/technical_spec.md.
// AWAITING_EXPERT is reachable from SUBMITTED and NEEDS_INFO as a fail-open bypass
// when the AI pipeline fails or max clarification cycles is reached (§4.4).
const ALLOWED_TRANSITIONS: Record<TicketStatus, readonly TicketStatus[]> = {
  DRAFT:             ['SUBMITTED'],
  SUBMITTED:         ['NEEDS_INFO', 'TRIAGED', 'AWAITING_EXPERT', 'RESPONDED'],
  NEEDS_INFO:        ['SUBMITTED', 'AWAITING_EXPERT', 'RESPONSE_DRAFTING', 'RESPONDED'],
  TRIAGED:           ['AWAITING_EXPERT', 'RESPONDED'],
  AWAITING_EXPERT:   ['EXPERT_REVIEWING', 'RESPONSE_DRAFTING', 'RESPONDED'],
  EXPERT_REVIEWING:  ['RESPONSE_DRAFTING', 'RESPONDED'],
  RESPONSE_DRAFTING: ['AWAITING_EXPERT', 'RESPONSE_READY', 'RESPONDED'],
  RESPONSE_READY:    ['RESPONSE_DRAFTING', 'RESPONDED'],
  RESPONDED:         ['AWAITING_REPLY', 'CLOSED'],
  AWAITING_REPLY:    ['SUBMITTED', 'RESPONDED', 'CLOSED'],
  CLOSED:            ['ARCHIVED'],
  ARCHIVED:          [],
}

export class InvalidTransitionError extends Error {
  constructor(from: TicketStatus, to: TicketStatus) {
    super(`Invalid ticket state transition: ${from} → ${to}`)
    this.name = 'InvalidTransitionError'
  }
}

/** Returns true if moving from → to is a defined allowed transition. */
export function canTransition(from: TicketStatus, to: TicketStatus): boolean {
  return (ALLOWED_TRANSITIONS[from] as readonly string[]).includes(to)
}

/**
 * Validates and performs the transition, returning the new status.
 * Throws InvalidTransitionError for any disallowed pair.
 */
export function transition(from: TicketStatus, to: TicketStatus): TicketStatus {
  if (!canTransition(from, to)) {
    throw new InvalidTransitionError(from, to)
  }
  return to
}

/** Returns every valid next state from the given status. */
export function getAllowedTransitions(from: TicketStatus): readonly TicketStatus[] {
  return ALLOWED_TRANSITIONS[from]
}

/** Returns true if this status has no valid outbound transitions. */
export function isTerminal(status: TicketStatus): boolean {
  return ALLOWED_TRANSITIONS[status].length === 0
}
